/**
 * Pure, DB-free helpers for rendering a flat list of rows as an expandable /
 * collapsible parent/child TREE.
 *
 * These power the shared `RecordTable` tree mode (opt-in `options.tree`). They are
 * generic over any row shape: the caller supplies two accessors — `getRowId` and
 * `getParentId` — so the same math serves the execution spawn tree
 * (`parent_execution_id`) and any future tree-shaped list.
 *
 * A row is attached under its parent iff that parent id is itself present in the
 * supplied set; a row whose parent is missing, null, self-referential, or outside
 * the set becomes a top-level root. Sibling order preserves input order (callers
 * pre-sort via the table order). The forest is a strict tree (each row has at most
 * one parent); the build is cycle-safe so malformed input always terminates and
 * every input row appears exactly once.
 *
 * Everything here is pure so it is unit-testable without a running workspace
 * (there is no local dev backend; jest is the CI-faithful check).
 */

/** Accessors that map a row to its own id and its parent's id. */
export interface TreeAccessors<T> {
    getRowId: (row: T) => string;
    getParentId: (row: T) => string | null | undefined;
}

export interface TreeNode<T> {
    id: string;
    row: T;
    children: TreeNode<T>[];
    /** 0 for a root of the returned forest, +1 per level below it. */
    depth: number;
}

/** A single row in the flattened, depth-annotated render order. */
export interface FlatTreeRow<T> {
    id: string;
    row: T;
    depth: number;
    hasChildren: boolean;
    /** Total descendants under this node (all levels), for a count badge. */
    descendantCount: number;
}

/**
 * Assemble a parent/child forest from a flat list of rows.
 *
 * Rules:
 * - Edges follow `getParentId(row)`. A child is attached under its parent iff that
 *   parent id is itself present in `rows`.
 * - A row whose parent id is missing, null, self-referential, or not present in
 *   the supplied set becomes a top-level root of the forest.
 * - Sibling order preserves input order (callers pre-sort via the table order).
 * - Cycle-safe: a node is never nested under any of its own descendants; if a
 *   cycle is detected the offending edge is dropped and the node is treated as a
 *   root, so the function always terminates and every input row appears exactly
 *   once.
 */
export function buildForest<T>(
    rows: readonly T[],
    { getRowId, getParentId }: TreeAccessors<T>
): TreeNode<T>[] {
    const nodeById = new Map<string, TreeNode<T>>();
    // First pass: create a node per row (last-wins on duplicate ids).
    for (const row of rows) {
        const id = getRowId(row);
        nodeById.set(id, { id, row, children: [], depth: 0 });
    }

    const roots: TreeNode<T>[] = [];

    const isAncestor = (
        candidateAncestorId: string,
        node: TreeNode<T>
    ): boolean => {
        // Walk up from `node` via already-linked parents to detect a cycle before
        // we attach it. Bounded by the current chain length.
        let current: string | null | undefined = getParentId(node.row);
        const guard = new Set<string>();
        while (current && !guard.has(current)) {
            if (current === candidateAncestorId) {
                return true;
            }
            guard.add(current);
            const parentNode = nodeById.get(current);
            current = parentNode ? getParentId(parentNode.row) : undefined;
        }
        return false;
    };

    // Second pass: link children to parents (or promote to root).
    for (const row of rows) {
        const id = getRowId(row);
        const node = nodeById.get(id);
        if (!node) {
            continue;
        }
        const parentId = getParentId(row);
        const parent = parentId ? nodeById.get(parentId) : undefined;
        if (parent && parentId !== id && !isAncestor(id, parent)) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    }

    // Third pass: stamp depth top-down (iterative to avoid deep recursion).
    const stack: TreeNode<T>[] = roots.map((r) => {
        r.depth = 0;
        return r;
    });
    while (stack.length) {
        const node = stack.pop() as TreeNode<T>;
        for (const child of node.children) {
            child.depth = node.depth + 1;
            stack.push(child);
        }
    }

    return roots;
}

/** Count all descendants (every level) under a node. */
export function countDescendants<T>(node: TreeNode<T>): number {
    let total = 0;
    const stack = [...node.children];
    while (stack.length) {
        const current = stack.pop() as TreeNode<T>;
        total += 1;
        for (const child of current.children) {
            stack.push(child);
        }
    }
    return total;
}

/**
 * Flatten a forest into depth-annotated rows in display order (pre-order DFS,
 * sibling order preserved), including a node's children only when the node id is
 * in `expandedIds`. Collapsed subtrees are omitted. This is what the tree view
 * maps over to render rows with indentation + an expand/collapse control.
 */
export function flattenForest<T>(
    roots: readonly TreeNode<T>[],
    expandedIds: ReadonlySet<string>
): FlatTreeRow<T>[] {
    const out: FlatTreeRow<T>[] = [];

    const visit = (node: TreeNode<T>) => {
        const hasChildren = node.children.length > 0;
        out.push({
            id: node.id,
            row: node.row,
            depth: node.depth,
            hasChildren,
            descendantCount: countDescendants(node),
        });
        if (hasChildren && expandedIds.has(node.id)) {
            for (const child of node.children) {
                visit(child);
            }
        }
    };

    for (const root of roots) {
        visit(root);
    }
    return out;
}

/** Collect every node id in the forest (for an expand-all affordance). */
export function collectAllIds<T>(roots: readonly TreeNode<T>[]): Set<string> {
    const ids = new Set<string>();
    const stack = [...roots];
    while (stack.length) {
        const node = stack.pop() as TreeNode<T>;
        ids.add(node.id);
        for (const child of node.children) {
            stack.push(child);
        }
    }
    return ids;
}
